import { Store } from "@ngxs/store";

import { PaisDTO, RespuestaEncuesta, RespuestaUsuario } from "@DTOS";
import { GeneroEnum, obtenerIdsRangoEdad, obtenerNombreRangoEdad } from "@shared/enums";
import { State } from "@shared/states";
import { Respuesta, RespuestaExtra } from "../../../layout/investigaciones/ver-respuestas/Respuesta";
import { ProcesoDatos } from "./ProcesoDatos";
import { Injectable } from "@angular/core";

@Injectable()
export class ProcesoDatosImp implements ProcesoDatos {
    public respuestasEnCrudo: RespuestaUsuario[] = [];
    public rangosEdad: Respuesta[] = [];
    public generos: Respuesta[] = [];
    public paises: Respuesta[] = [];
    public regiones: Respuesta[] = [];
    public ciudades: Respuesta[] = [];

    public respuestasProcesadas: Map<number, RespuestaExtra> = new Map();

    private indiceRespuestasLeido = 0;
    private cantidadRespuestas = 0;

    private paisesTodos: PaisDTO[];

    constructor(private store: Store) {}

    public cargarDatos() {
        this.limpiar();
        this.paisesTodos = this.store.selectSnapshot((state: State) => state.paises.paises);
    }

    public procesar(respuestasEncuesta: RespuestaEncuesta[]) {
        respuestasEncuesta.forEach(respuestaEncuesta => {
            this.procesarRespuestas(respuestaEncuesta);
        });
    }

    public procesarDesde(respuestasUsuario: RespuestaUsuario[]) {
        this.cantidadRespuestas = respuestasUsuario.length;

        respuestasUsuario.forEach(datosRespuestasUsuario => {
            this.procesarRespuestasUsuario(datosRespuestasUsuario);
            this.indiceRespuestasLeido++;
        });
        this.recalcularValores();
    }

    private procesarRespuestas(respuestaEncuesta: RespuestaEncuesta) {
        this.respuestasEnCrudo = this.respuestasEnCrudo.concat(respuestaEncuesta.respuestasDeUsuarios);
        this.cantidadRespuestas = respuestaEncuesta.respuestasDeUsuarios.length;
        respuestaEncuesta.respuestasDeUsuarios.forEach(respuestaUsuario => {
            this.procesarRespuestasUsuario(respuestaUsuario);
            this.indiceRespuestasLeido++;
        });
        this.recalcularValores();
    }

    private recalcularValores() {
        this.rangosEdad.forEach(rangoEdad => rangoEdad.value = (rangoEdad.value ?? 0) / this.cantidadRespuestas);
        this.generos.forEach(genero => genero.value = (genero.value ?? 0) / this.cantidadRespuestas);
        this.paises.forEach(pais => pais.value = (pais.value ?? 0) / this.cantidadRespuestas);
    }

    private procesarRespuestasUsuario(respuestaUsuario: RespuestaUsuario) {
        this.procesarEdades(respuestaUsuario);
        this.procesarGenero(respuestaUsuario);
        this.procesarPais(respuestaUsuario);
        this.procesarRegion(respuestaUsuario);
        this.procesarCiudad(respuestaUsuario);
        this.procesarRespuestasItems(respuestaUsuario);
    }

    private procesarEdades(respuestaEncuesta: RespuestaUsuario) {
        const rangosEdadArray = obtenerIdsRangoEdad();
        let cortar = false;

        for (let i = 0; i < rangosEdadArray.length && !cortar; i++) {
            if (rangosEdadArray[i][0] !== respuestaEncuesta.rangoEdad) {
                continue;
            }
            cortar = true;

            let cantidadNueva = 1;
            let indicesRespuestaCorrecta: number[] = [];
            let usuarios: Set<number> = new Set();

            if (this.rangosEdad[i]) {
                cantidadNueva = (this.rangosEdad[i].extra?.cantidad ?? 0) + 1;
                indicesRespuestaCorrecta = this.rangosEdad[i]?.extra?.indices ?? [];
                usuarios = this.rangosEdad[i].extra?.usuarios ?? new Set();
            }

            indicesRespuestaCorrecta.push(this.indiceRespuestasLeido);
            usuarios.add(respuestaEncuesta.id);

            this.rangosEdad[i] = {
                name: obtenerNombreRangoEdad(rangosEdadArray[i][0]),
                value: cantidadNueva,
                extra: {
                    cantidad: cantidadNueva,
                    indices: indicesRespuestaCorrecta,
                    usuarios
                }
            };
        }
    }

    private procesarGenero(respuestaUsuario: RespuestaUsuario) {
        let cantidadNueva = 1;
        let index = 0;
        let nombre = "Masculino";
        let indicesRespuestaCorrecta: number[] = [];
        let usuarios: Set<number> = new Set();

        if (respuestaUsuario.genero == null) {
            return;
        }

        switch (respuestaUsuario.genero) {
        case GeneroEnum.MALE:
            if (this.generos[0]) {
                cantidadNueva = (this.generos[0].extra?.cantidad ?? 0) + 1;
                indicesRespuestaCorrecta = this.generos[0].extra?.indices ?? [];
                usuarios = this.generos[0].extra?.usuarios ?? new Set();
            }
            break;
        case GeneroEnum.FEMALE:
            if (this.generos[1]) {
                cantidadNueva = (this.generos[1].extra?.cantidad ?? 0) + 1;
                indicesRespuestaCorrecta = this.generos[1].extra?.indices ?? [];
                usuarios = this.generos[1].extra?.usuarios ?? new Set();
            }

            index = 1;
            nombre = "Femenino";
            break;
        case GeneroEnum.NONBIN:
            return;
        }

        indicesRespuestaCorrecta.push(this.indiceRespuestasLeido);
        usuarios.add(respuestaUsuario.id);

        this.generos[index] = {
            name: nombre,
            value: cantidadNueva,
            extra: {
                cantidad: cantidadNueva,
                indices: indicesRespuestaCorrecta,
                usuarios
            }
        };
    }

    private procesarPais(respuestaUsuario: RespuestaUsuario) {
        const paises = this.paisesTodos;

        let cortar = false;

        for (let i = 0; i < paises.length && !cortar; i++) {
            if (respuestaUsuario.pais == null) {
                continue;
            }

            if (respuestaUsuario.pais.codigo === paises[i].codigo) {
                cortar = true;

                let cantidadNueva = 1;
                let indicesRespuestaCorrecta: number[] = [];
                let usuarios: Set<number> = new Set();

                if (this.paises[i]) {
                    cantidadNueva = (this.paises[i].extra?.cantidad ?? 0) + 1;
                    indicesRespuestaCorrecta = this.paises[i].extra?.indices ?? [];
                    usuarios = this.paises[i].extra?.usuarios ?? new Set();
                }

                indicesRespuestaCorrecta.push(this.indiceRespuestasLeido);
                usuarios.add(respuestaUsuario.id);

                this.paises[i] = {
                    name: paises[i].nombre,
                    value: cantidadNueva,
                    extra: {
                        cantidad: cantidadNueva,
                        indices: indicesRespuestaCorrecta,
                        usuarios
                    }
                };
            }
        }
    }

    private procesarRegion(respuestaUsuario: RespuestaUsuario): void {
        if (respuestaUsuario.region == null) {
            return;
        }

        let indice = -1;
        let cantidad = 0;
        let indiceRespuestasLeido: number[] = [];
        let usuarios: Set<number> = new Set();

        if (this.regiones.some(region => region.name === respuestaUsuario.region?.nombre)) {
            indice = this.regiones.findIndex(region => region.name === respuestaUsuario.region?.nombre);
            const respuestaGuardada = this.regiones[indice];
            const extra = respuestaGuardada.extra;

            cantidad = extra?.cantidad ?? 0;
            indiceRespuestasLeido = extra?.indices ?? [];
            usuarios = extra?.usuarios ?? new Set();
        }

        indiceRespuestasLeido.push(this.indiceRespuestasLeido);
        usuarios.add(respuestaUsuario.id);

        const respuesta: Respuesta = {
            name: respuestaUsuario.region.nombre,
            value: 0,
            extra: {
                cantidad: ++cantidad,
                indices: indiceRespuestasLeido,
                usuarios
            }
        };

        if (indice !== -1) {
            this.regiones[indice] = respuesta;
            return;
        }

        this.regiones.push(respuesta);
    }
    private procesarCiudad(respuestaUsuario: RespuestaUsuario) {
        if (respuestaUsuario.ciudad == null) {
            return;
        }

        let indice = -1;
        let cantidad = 0;
        let indiceRespuestasLeido: number[] = [];
        let usuarios: Set<number> = new Set();

        if (this.ciudades.some(ciudad => ciudad.name === respuestaUsuario.ciudad?.nombre)) {
            indice = this.ciudades.findIndex(ciudad => ciudad.name === respuestaUsuario.ciudad?.nombre);
            const respuestaGuardada = this.ciudades[indice];
            const extra = respuestaGuardada.extra;

            cantidad = extra?.cantidad ?? 0;
            indiceRespuestasLeido = extra?.indices ?? [];
            usuarios = extra?.usuarios ?? new Set();
        }

        indiceRespuestasLeido.push(this.indiceRespuestasLeido);
        usuarios.add(respuestaUsuario.id);

        const respuesta: Respuesta = {
            name: respuestaUsuario.ciudad.nombre,
            value: 0,
            extra: {
                cantidad: ++cantidad,
                indices: indiceRespuestasLeido,
                usuarios
            }
        };

        if (indice !== -1) {
            this.ciudades[indice] = respuesta;
            return;
        }

        this.ciudades.push(respuesta);
    }

    private procesarRespuestasItems(respuestaUsuario: RespuestaUsuario) {
        respuestaUsuario.respuestas.forEach(respuesta => {
            const idItem = respuesta[0];

            let cantidad = 0;
            let indiceRespuestasLeido: number[] = [];
            let valores: string[] = [];
            let valoresRango: Map<number, number> = new Map();
            let usuarios: Set<number> = new Set();

            if (this.respuestasProcesadas.has(idItem)) {
                const respuestaExtraGuardada = this.respuestasProcesadas.get(idItem);
                if (respuestaExtraGuardada != null) {
                    cantidad = respuestaExtraGuardada.cantidad;
                    indiceRespuestasLeido = respuestaExtraGuardada.indices;
                    valores = respuestaExtraGuardada.valores ?? [];
                    usuarios = respuestaExtraGuardada.usuarios;

                    valoresRango = respuestaExtraGuardada.valoresRango ?? new Map();
                }
            }

            indiceRespuestasLeido.push(this.indiceRespuestasLeido);
            usuarios.add(respuestaUsuario.id);

            // Es de tipo texto libre
            if (respuesta[1]) {
                valores.push(respuesta[1]);
            }

            // Es de tipo Rango o ranking
            if (respuesta[2]) {
                let cantidadValorRango = 0;

                if (valoresRango.has(respuesta[2])) {
                    cantidadValorRango = valoresRango.get(respuesta[2]) ?? 0;
                }

                valoresRango.set(respuesta[2], ++cantidadValorRango);
            }

            const respuestaExtra: RespuestaExtra = {
                cantidad: ++cantidad,
                indices: indiceRespuestasLeido,
                valores,
                valoresRango,
                usuarios

            };

            this.respuestasProcesadas.set(idItem, respuestaExtra);
        });
    }

    private limpiar() {
        this.respuestasEnCrudo = [];
        this.rangosEdad = [];
        this.generos = [];
        this.paises = [];
        this.regiones = [];
        this.ciudades = [];

        this.respuestasProcesadas = new Map();

        this.indiceRespuestasLeido = 0;
        this.cantidadRespuestas = 0;
    }
}
